import { Icon } from '@iconify/react';
import { Table } from '@tanstack/react-table';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/Domain/components/Shared/DropdownMenu/DropdownMenu';
import { Input } from '@/Domain/components/Shared/Input/Input';

type Props<TData> = {
  table: Table<TData>;
};

export const DataTableViewOptions = <TData,>({ table }: Props<TData>) => {
  const { t } = useTranslation('knowledge_base');
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="white" size="icon" className="ml-auto">
          <Icon icon="mingcute:column-line" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[200px]">
        <div className="pb-1">
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(event) => event.stopPropagation()}
            placeholder="Search..."
            size="sm"
            startIcon={<Icon icon="mingcute:search-line" />}
          />
        </div>
        {table
          .getAllColumns()
          .filter((column) => typeof column.accessorFn !== 'undefined' && column.getCanHide())
          .map((column) => {
            if (searchQuery && !column.id.toLowerCase().includes(searchQuery.toLowerCase())) {
              return null;
            }
            const label = (column.columnDef.meta as { label?: string } | undefined)?.label;
            const displayName = label ?? column.id.replace(/_/g, ' ');
            return (
              <DropdownMenuCheckboxItem
                key={column.id}
                className="capitalize"
                checked={column.getIsVisible()}
                onCheckedChange={(value) => column.toggleVisibility(!!value)}
                onSelect={(e) => e.preventDefault()}
              >
                {displayName}
              </DropdownMenuCheckboxItem>
            );
          })}
        <DropdownMenuSeparator />
        <DropdownMenuCheckboxItem
          onClick={() => {
            table.resetColumnVisibility();
            setSearchQuery('');
          }}
          onSelect={(e) => e.preventDefault()}
        >
          <Icon icon="mingcute:refresh-2-line" className="mr-2 h-4 w-4" />
          {t('articles.list.filters.reset')}
        </DropdownMenuCheckboxItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
